import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  type CalendarEventIteratorFilter,
  Iterator,
  type UseQueryReturnType,
  gqlClient,
  graphql,
  queryClient,
} from '@tyro/api';
import dayjs from 'dayjs';
import { groupsKeys } from './keys';

// Query for getting closest/prev/next lesson for a subject group
const subjectGroupLessonByIterator = graphql(/* GraphQL */ `
  query calendar_calendarEventsIterator($filter: CalendarEventIteratorFilter!) {
    calendar_calendarEventsIterator_v2(filter: $filter) {
      event {
        eventId
        name
        colour
        calendarIds
        startTime
        endTime
        type
        attendees {
          partyId
          type
          partyInfo {
            partyId
            ... on Staff {
              person {
                firstName
                lastName
                avatarUrl
                type
                title {
                  id
                  name
                  nameTextId
                }
              }
            }
          }
        }
        rooms {
          name
        }
        extensions {
          eventAttendance {
            eventId
            note
            attendanceCodeId
            attendanceCode {
              name
              codeType
            }
            personPartyId
            adminSubmitted
            createdBy {
              partyId
              firstName
              lastName
              type
              avatarUrl
              title {
                id
                name
                nameTextId
              }
            }
            updatedAt
            updatedBy {
              partyId
              firstName
              lastName
              type
              avatarUrl
              title {
                id
                name
                nameTextId
              }
            }
            groupAttendanceTaken
          }
          previousEventAttendance {
            attendanceCode {
              codeType
            }
            personPartyId
          }
        }
      }
      eventsOnSameDayForSameGroup {
        eventId
        name
        colour
        type
        startTime
        endTime
        rooms {
          name
        }
        tags {
          label
          context
        }
      }
    }
  }
`);

const subjectGroupLessonQuery = (filter: CalendarEventIteratorFilter) => ({
  queryKey: groupsKeys.subject.lesson(filter),
  queryFn: async () => {
    const { calendar_calendarEventsIterator_v2: eventData } =
      await gqlClient.request(subjectGroupLessonByIterator, { filter });

    const [lastUpdate] = (eventData?.event?.extensions?.eventAttendance || [])
      .filter((event) => !event?.adminSubmitted)
      .sort(
        (eventA, eventB) =>
          dayjs(eventB?.updatedAt).unix() - dayjs(eventA?.updatedAt).unix(),
      );
    const isAttendanceTaken =
      eventData?.event?.extensions?.eventAttendance?.some(
        (event) => !!event?.groupAttendanceTaken,
      );

    const additionalLessons = eventData?.eventsOnSameDayForSameGroup || [];

    return {
      calendar_calendarEventsIterator: eventData?.event
        ? {
            ...eventData?.event,
            updatedAt: lastUpdate?.updatedAt,
            updatedBy: lastUpdate?.updatedBy,
            isAttendanceTaken,
            additionalLessons: additionalLessons.sort(
              (eventA, eventB) =>
                dayjs(eventA.startTime).unix() - dayjs(eventB.startTime).unix(),
            ),
          }
        : undefined,
    };
  },
});

export function getSubjectGroupLesson(filter: CalendarEventIteratorFilter) {
  return queryClient.fetchQuery(subjectGroupLessonQuery(filter));
}

export function useNextSubjectGroupLesson(filter: CalendarEventIteratorFilter) {
  const { data: closestLessonData } = useSubjectGroupLessonByIterator({
    ...filter,
    iterator: Iterator.Closest,
  });

  return useQuery({
    ...subjectGroupLessonQuery({
      ...filter,
      iterator: Iterator.Next,
      eventStartTime: closestLessonData?.startTime,
    }),
    enabled: !!closestLessonData?.eventId,
    select: ({ calendar_calendarEventsIterator }) =>
      calendar_calendarEventsIterator,
  });
}

export function useSubjectGroupLessonByIterator(
  filter: CalendarEventIteratorFilter,
) {
  return useQuery({
    ...subjectGroupLessonQuery(filter),
    placeholderData: keepPreviousData,
    select: ({ calendar_calendarEventsIterator }) =>
      calendar_calendarEventsIterator,
  });
}

export type ReturnTypeFromUseSubjectGroupLessonByIterator = UseQueryReturnType<
  typeof useSubjectGroupLessonByIterator
>;
